import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MessageInboxItem, MessageItemDetails, MeyMessageInboxClient, ReplyToInboxMessageRequest, EmailClassificationClient, EmailClassificationCategory } from '../../../web-api-client';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'app-read-mail',
  templateUrl: './read-mail.component.html',
  styleUrls: ['./read-mail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: '0', opacity: 0, overflow: 'hidden' }))
      ])
    ])
  ]
})
export class ReadMailComponent implements OnInit, OnDestroy {
  @ViewChild('replySection') replySection: ElementRef;
  @ViewChild('replyTextarea') replyTextarea: ElementRef;

  private destroy$ = new Subject<void>();
  public onClose: Subject<void> = new Subject();
  public message: MessageInboxItem;

  public messageDetails: MessageItemDetails;
  public loading = true;
  public drawerOpen = false;
  public showDetails = false;

  // Reply functionality
  public showReplyForm: boolean = false;
  public replySubject: string = '';
  public replyBody: string = '';
  public replyAttachments: File[] = [];
  public uploadedBlobIds: number[] = [];
  public isSendingReply: boolean = false;
  public replyError: string | null = null;

  // Reclassification functionality
  public selectedClassification: EmailClassificationCategory | null = null;
  public isReclassifying: boolean = false;
  public reclassificationError: string | null = null;
  public EmailClassificationCategory = EmailClassificationCategory; // Expose enum to template

  constructor(
    public client: MeyMessageInboxClient,
    public classificationClient: EmailClassificationClient,
    public changeDetecRef: ChangeDetectorRef,
    public activeModal: NgbActiveModal,
    private sanitizer: DomSanitizer
  ) {

  }

  ngOnInit(): void {
    this.client.getMessageDetails(this.message.messageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(item => {
        this.messageDetails = item;
        this.loading = false;

        // Initialize selected classification from message
        const latestClassification = this.getLatestClassification();
        if (latestClassification) {
          this.selectedClassification = latestClassification.category;
        }

        this.changeDetecRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.onClose.complete();
  }

  closeModal(): void {
    this.onClose.next();
    this.activeModal.close();
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
    this.changeDetecRef.detectChanges();
  }

  scrollToReply(): void {
    this.showReplyForm = true;
    this.changeDetecRef.detectChanges();

    setTimeout(() => {
      if (this.replySection) {
        this.replySection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      this.focusReplyBox();
    }, 100);
  }

  focusReplyBox(): void {
    setTimeout(() => {
      if (this.replyTextarea) {
        this.replyTextarea.nativeElement.focus();
      }
    }, 150);
  }

  getSanitizedMessage(): string {
    if (!this.messageDetails?.message) return 'Няма съдържание';
    // Use innerText binding instead of innerHTML for security
    return this.messageDetails.message;
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'fa-file-o';

    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'fa-file-pdf-o';
      case 'doc':
      case 'docx':
        return 'fa-file-word-o';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel-o';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fa-file-image-o';
      case 'zip':
      case 'rar':
        return 'fa-file-archive-o';
      default:
        return 'fa-file-o';
    }
  }

  toggleAttachmentDetails(attachment: any): void {
    attachment.showDetails = !attachment.showDetails;
    this.changeDetecRef.detectChanges();
  }

  openFile(url: string): void {
    window.open(url, '_blank');
  }

  formatDate(date: any): string {
    if (!date) return 'Няма дата';

    const d = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - d.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    if (diffDays === 0) {
      return 'Днес, ' + d.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Вчера, ' + d.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' });
    } else {
      return d.toLocaleDateString('bg-BG', options);
    }
  }

  hasFields(): boolean {
    return this.messageDetails?.fields && Object.keys(this.messageDetails.fields).length > 0;
  }

  getFieldsArray(): Array<{key: string, value: any}> {
    if (!this.messageDetails?.fields) return [];

    return Object.keys(this.messageDetails.fields).map(key => ({
      key: key,
      value: this.formatFieldValue(this.messageDetails.fields[key])
    }));
  }

  formatFieldValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return 'N/A';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = (bytes / Math.pow(1024, i)).toFixed(2);

    return `${size} ${sizes[i]}`;
  }

  toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;
    this.changeDetecRef.detectChanges();
  }

  getParsedFields(): any[] {
    if (!this.messageDetails?.fields) return [];

    const parsedFields: any[] = [];

    Object.keys(this.messageDetails.fields).forEach(key => {
      const value = this.messageDetails.fields[key];

      // Try to parse JSON strings
      if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
        try {
          const parsed = JSON.parse(value);
          // Skip attachment arrays - they'll be shown in the attachments section
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].blobId) {
            return; // Skip this field
          }
          parsedFields.push({
            key: key,
            value: parsed,
            isArray: Array.isArray(parsed),
            isParsed: true
          });
        } catch (e) {
          parsedFields.push({
            key: key,
            value: value,
            isArray: false,
            isParsed: false
          });
        }
      } else {
        parsedFields.push({
          key: key,
          value: value,
          isArray: false,
          isParsed: false
        });
      }
    });

    return parsedFields;
  }

  getEnhancedAttachments(): any[] {
    if (!this.messageDetails?.attachments) return [];

    // Try to find detailed attachment info from fields
    let detailedAttachments: any[] = [];

    if (this.messageDetails.fields) {
      Object.keys(this.messageDetails.fields).forEach(key => {
        const value = this.messageDetails.fields[key];

        // Try to parse JSON strings that might contain attachment details
        if (typeof value === 'string' && value.startsWith('[')) {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].blobId) {
              detailedAttachments = parsed;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      });
    }

    // If we have detailed attachments, return them; otherwise return basic attachments
    return detailedAttachments.length > 0 ? detailedAttachments : this.messageDetails.attachments;
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('bg-BG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }

  getRawFieldsJson(): string {
    if (!this.messageDetails?.fields) return '{}';
    return JSON.stringify(this.messageDetails.fields, null, 2);
  }

  toggleReplyForm(): void {
    this.showReplyForm = !this.showReplyForm;
    if (this.showReplyForm) {
      this.replySubject = this.messageDetails.subject.startsWith('Re: ')
        ? this.messageDetails.subject
        : `Re: ${this.messageDetails.subject}`;
      this.replyBody = '';
      this.replyAttachments = [];
      this.uploadedBlobIds = [];
      this.replyError = null;
      this.focusReplyBox();
    }
    this.changeDetecRef.detectChanges();
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      this.replyAttachments = Array.from(files);
    }
  }

  async sendReply(): Promise<void> {
    if (!this.replySubject || !this.replySubject.trim()) {
      this.replyError = 'Темата е задължителна';
      this.changeDetecRef.detectChanges();
      return;
    }
    if (!this.replyBody || !this.replyBody.trim()) {
      this.replyError = 'Съдържанието на съобщението е задължително';
      this.changeDetecRef.detectChanges();
      return;
    }

    this.isSendingReply = true;
    this.replyError = null;
    this.changeDetecRef.detectChanges();

    try {
      const recipientProfileId = this.messageDetails.sender?.profileId;
      if (!recipientProfileId) {
        throw new Error('Не може да се определи получателят');
      }

      const request = new ReplyToInboxMessageRequest({
        originalMessageId: this.message.messageId,
        recipientProfileIds: [recipientProfileId],
        messageBody: this.replyBody,
        messageSubject: this.replySubject,
        attachmentBlobIds: this.uploadedBlobIds.length > 0 ? this.uploadedBlobIds : undefined
      });

      // Use pipe and takeUntil instead of deprecated toPromise()
      await new Promise((resolve, reject) => {
        this.client.replyToInboxMessage(request)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response && response.result) {
                // Success - show notification (TODO: replace with proper notification service)
                alert('Отговорът е изпратен успешно!');
                this.showReplyForm = false;
                this.closeModal();
                resolve(response);
              } else {
                reject(new Error(response?.errorMessage || 'Неуспешно изпращане на отговор'));
              }
            },
            error: (error) => reject(error)
          });
      });
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      this.replyError = error.message || 'Неуспешно изпращане на отговор. Моля, опитайте отново.';
    } finally {
      this.isSendingReply = false;
      this.changeDetecRef.detectChanges();
    }
  }

  cancelReply(): void {
    if (!this.replyBody && !this.replySubject) {
      // Nothing to lose, just close
      this.showReplyForm = false;
      this.changeDetecRef.detectChanges();
      return;
    }

    // TODO: Replace with proper modal confirmation service
    if (confirm('Сигурни ли сте, че искате да откажете отговора? Съобщението ще бъде изгубено.')) {
      this.showReplyForm = false;
      this.replyBody = '';
      this.replySubject = '';
      this.replyAttachments = [];
      this.uploadedBlobIds = [];
      this.replyError = null;
      this.changeDetecRef.detectChanges();
    }
  }

  /**
   * Handle classification change from dropdown
   */
  onClassificationChange(newCategory: EmailClassificationCategory): void {
    if (this.isReclassifying) {
      return; // Prevent multiple simultaneous requests
    }

    // If same as current, do nothing
    if (this.selectedClassification === newCategory) {
      return;
    }

    this.isReclassifying = true;
    this.reclassificationError = null;
    this.changeDetecRef.detectChanges();

    this.classificationClient.reclassifyEmail(this.message.messageId, newCategory)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Update local state
          this.selectedClassification = newCategory;

          // Create new classification object for the reclassification
          const newClassification: any = {
            messageInboxItemId: this.message.id,
            category: newCategory,
            isManualClassification: true,
            classifiedAt: new Date()
          };

          // Add to emailClassifications array (or create array if it doesn't exist)
          if (!this.message.emailClassifications) {
            this.message.emailClassifications = [];
          }
          this.message.emailClassifications.push(newClassification);

          this.isReclassifying = false;
          this.changeDetecRef.detectChanges();
        },
        error: (error) => {
          console.error('Failed to reclassify email:', error);
          this.reclassificationError = 'Неуспешна промяна на класификацията. Моля, опитайте отново.';
          this.isReclassifying = false;
          this.changeDetecRef.detectChanges();
        }
      });
  }

  /**
   * Get display text for classification category
   */
  getClassificationLabel(category: EmailClassificationCategory | null): string {
    if (category === null || category === undefined) {
      return 'Некласифицирано';
    }

    switch (category) {
      case EmailClassificationCategory.Zayavlenie:
        return 'Заявление';
      case EmailClassificationCategory.Other:
        return 'Други';
      case EmailClassificationCategory.Unknown:
      default:
        return 'Неизвестно';
    }
  }

  /**
   * Get badge color for classification category
   */
  getClassificationBadgeClass(category: EmailClassificationCategory | null): string {
    if (category === null || category === undefined) {
      return 'bg-secondary';
    }

    switch (category) {
      case EmailClassificationCategory.Zayavlenie:
        return 'bg-success';
      case EmailClassificationCategory.Other:
        return 'bg-info';
      case EmailClassificationCategory.Unknown:
      default:
        return 'bg-warning';
    }
  }

  /**
   * Get the latest classification for the message
   */
  private getLatestClassification(): any | undefined {
    if (!this.message.emailClassifications || this.message.emailClassifications.length === 0) {
      return undefined;
    }

    // Return the most recent classification (sorted by classifiedAt descending)
    return this.message.emailClassifications
      .sort((a, b) => {
        const dateA = a.classifiedAt ? new Date(a.classifiedAt).getTime() : 0;
        const dateB = b.classifiedAt ? new Date(b.classifiedAt).getTime() : 0;
        return dateB - dateA;
      })[0];
  }

}
